import type { AppLocale } from '../i18n';
import { parseUtcDateString } from './formatUtcInTimeZone';
import { resolveIntlTimeZoneId } from './resolveIntlTimeZone';

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

function toPersianDigits(input: string): string {
  return input.replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)] ?? d);
}

function resolveAppDate(value: Date | string | number): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const parsedUtc = parseUtcDateString(value);
    if (parsedUtc) return parsedUtc;
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export interface FormatDateOptions extends Intl.DateTimeFormatOptions {
  /** When true and locale is fa, use Persian calendar month names. */
  usePersianCalendar?: boolean;
}

export function formatAppDate(
  value: Date | string | number,
  locale: AppLocale,
  options: FormatDateOptions = {}
): string {
  const date = resolveAppDate(value);
  if (!date) return '';

  const { usePersianCalendar = true, ...intlOptions } = options;
  const intlLocale = locale === 'fa' ? 'fa-IR' : 'en-US';
  const formatOptions: Intl.DateTimeFormatOptions = {
    ...intlOptions,
    ...(locale === 'fa' && usePersianCalendar ? { calendar: 'persian' } : {}),
  };

  try {
    const formatted = new Intl.DateTimeFormat(intlLocale, formatOptions).format(date);
    return locale === 'fa' ? toPersianDigits(formatted) : formatted;
  } catch {
    return date.toLocaleDateString(intlLocale, intlOptions);
  }
}

/** Locale-aware date + time; parses API UTC strings and uses Gregorian calendar for wall-clock timestamps. */
export function formatAppDateTime(
  value: Date | string | number,
  locale: AppLocale,
  timeZoneId?: string | null
): string {
  const timeZone = timeZoneId ? resolveIntlTimeZoneId(timeZoneId) : undefined;
  return formatAppDate(value, locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    usePersianCalendar: false,
    ...(timeZone ? { timeZone } : {}),
  });
}

export function formatAppRelativeDay(value: Date | string | number, locale: AppLocale): string {
  return formatAppDate(value, locale, { weekday: 'long', month: 'long', day: 'numeric' });
}
